import React from 'react';
import { Box, CardContent, Typography, Collapse, CircularProgress, Alert } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { CollapsibleMaintenanceCard, ExpandMoreStyled } from '../ProfileTab.styles';
import { BRAND_COLORS } from '../../../../styles/theme';
import { AnalysisResult } from '../types/health.types';
import { AnalysisDisplay } from './AnalysisDisplays';
import { InfoButton } from './InfoButton';

interface HealthPanelCardProps {
  title: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  analyses?: AnalysisResult[];
  loading?: boolean;
  error?: string;
  children?: React.ReactNode;
  infoContent?: string;
}

export const HealthPanelCard: React.FC<HealthPanelCardProps> = ({
  title,
  expanded,
  onToggleExpanded,
  analyses,
  loading = false,
  error,
  children,
  infoContent,
}) => {
  const hasAnalyses = analyses && analyses.length > 0;

  // Check if any analysis has warning status
  const hasWarning = analyses?.some((analysis) => {
    if (analysis.type === 'AVGT') {
      return analysis.status === 'warning';
    }
    if (analysis.type === 'AVGR') {
      return analysis.status === 'warning';
    }
    return false;
  });

  return (
    <CollapsibleMaintenanceCard $overdue={false}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={onToggleExpanded}
        >
          <Typography
            variant="h6"
            sx={{ color: hasWarning ? '#ed6c02' : BRAND_COLORS.forestGreen.main }}
          >
            {title}
          </Typography>
          {infoContent && <InfoButton title={title} content={infoContent} />}
          <ExpandMoreStyled
            $expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
          >
            <ExpandMoreIcon />
          </ExpandMoreStyled>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && hasAnalyses && (
              <Box>
                {analyses.map((analysis, index) => (
                  <AnalysisDisplay key={index} analysis={analysis} />
                ))}
              </Box>
            )}

            {!loading && !error && !hasAnalyses && !children && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                {title} monitoring coming soon...
              </Typography>
            )}

            {!loading && !error && children}
          </Box>
        </Collapse>
      </CardContent>
    </CollapsibleMaintenanceCard>
  );
};
